import { useState, useEffect, useMemo } from 'react'
import { collection, query, where, onSnapshot } from 'firebase/firestore'
import { getFirebaseDb, isFirebaseAvailable } from '../lib/firebaseInit'
import { CHATROOM_PATH, CHAT_FIELDS, CHAT_PATHSEGMENTS } from '@/constants/chat'
import { getUserChatRooms } from '@/utils/chatUtils'

export interface UnreadMessagesData {
  unreadCount: number
  unreadRoomsCount: number
  unreadByRoom: Record<string, number>
  loading: boolean
}

export const useUnreadMessages = (userId: string): UnreadMessagesData => {
  const [unreadCount, setUnreadCount] = useState(0)
  const [unreadRoomsCount, setUnreadRoomsCount] = useState(0)
  const [unreadByRoom, setUnreadByRoom] = useState<Record<string, number>>({})
  const [loading, setLoading] = useState(true)
  const [recentChatRoomId, setRecentChatRoomId] = useState<string | undefined>(
    undefined
  )
  const [allRoomIds, setAllRoomIds] = useState<string[]>([])
  const roomIdsKey = useMemo(() => allRoomIds.join(','), [allRoomIds])

  useEffect(() => {
    const fetchUserChatRooms = async () => {
      try {
        const rooms = await getUserChatRooms(userId)

        if (rooms.length > 0) {
          const mostRecentRoom = rooms[0]
          setRecentChatRoomId(mostRecentRoom.id as string)

          const roomIds = rooms.map(room => room.id as string)
          setAllRoomIds(roomIds)
        }
      } catch (error) {
        console.error('Error fetching user chat rooms:', error)
      }
    }

    if (userId) {
      fetchUserChatRooms()
    }
  }, [userId])

  useEffect(() => {
    if (!userId || !recentChatRoomId) {
      setLoading(false)
      return
    }

    if (!isFirebaseAvailable()) {
      setLoading(false)
      return
    }

    const db = getFirebaseDb()
    if (!db) {
      setLoading(false)
      return
    }

    try {
      const messagesRef = collection(
        db,
        CHATROOM_PATH,
        recentChatRoomId,
        CHAT_PATHSEGMENTS.MESSAGES
      )

      const firebaseQuery = query(
        messagesRef,
        where(CHAT_FIELDS.SENDERID, '!=', userId),
        where(CHAT_FIELDS.READ, '==', false)
      )

      const unsubscribe = onSnapshot(
        firebaseQuery,
        snapshot => {
          setUnreadCount(snapshot.size)
          setLoading(false)
        },
        error => {
          console.error('Error fetching unread messages:', error)
          setUnreadCount(0)
          setLoading(false)
        }
      )

      return () => unsubscribe()
    } catch (error) {
      console.error('Error setting up unread messages listener:', error)
      setLoading(false)
    }
  }, [userId, recentChatRoomId])

  useEffect(() => {
    if (!userId || allRoomIds.length === 0) {
      return
    }

    if (!isFirebaseAvailable()) {
      return
    }

    const db = getFirebaseDb()
    if (!db) {
      return
    }

    const unsubscribers: (() => void)[] = []

    try {
      allRoomIds.forEach(roomId => {
        const messagesRef = collection(
          db,
          CHATROOM_PATH,
          roomId,
          CHAT_PATHSEGMENTS.MESSAGES
        )

        const firebaseQuery = query(
          messagesRef,
          where(CHAT_FIELDS.SENDERID, '!=', userId),
          where(CHAT_FIELDS.READ, '==', false)
        )

        const unsubscribe = onSnapshot(
          firebaseQuery,
          snapshot => {
            const count = snapshot.size

            setUnreadByRoom(prev => {
              const updated = { ...prev, [roomId]: count }
              return updated
            })
          },
          error => {
            console.error(
              `Error fetching unread messages for room ${roomId}:`,
              error
            )
            setUnreadByRoom(prev => {
              const updated = { ...prev }
              delete updated[roomId]
              return updated
            })
          }
        )

        unsubscribers.push(unsubscribe)
      })
    } catch (error) {
      console.error('Error setting up unread messages listeners:', error)
    }

    return () => {
      unsubscribers.forEach(unsubscribe => unsubscribe())
    }
  }, [userId, roomIdsKey])

  useEffect(() => {
    const roomsWithUnread = Object.values(unreadByRoom).filter(
      c => c > 0
    ).length
    setUnreadRoomsCount(roomsWithUnread)
  }, [unreadByRoom])

  return {
    unreadCount,
    unreadRoomsCount,
    unreadByRoom,
    loading,
  }
}
